import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, LoaderCircle, LockKeyhole } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { api, ApiClientError } from '../api'
import i18n, { supportedLanguages } from '../i18n'
import { LanguageControl } from './language-control'

const requestStoragePrefix = 'computinger-developer-sso:'
const requestLifetimeMs = 5 * 60 * 1_000

type StoredAuthorizationRequest = {
  state: string
  verifier: string
  redirectUri: string
  returnTo: string
  createdAt: number
}

function randomBase64Url(bytes: number): string {
  const value = crypto.getRandomValues(new Uint8Array(bytes))
  return btoa(String.fromCharCode(...value))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/u, '')
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  )
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/u, '')
}

function safeReturnTo(value: string | null): string {
  if (!value?.startsWith('/') || value.startsWith('//') || value.startsWith('/auth/')) {
    return '/projects'
  }
  const target = new URL(value, window.location.origin)
  return target.origin === window.location.origin
    ? `${target.pathname}${target.search}${target.hash}`
    : '/projects'
}

function AuthFrame({
  title,
  description,
  busy = false,
  danger = false,
  action,
}: {
  title: string
  description: string
  busy?: boolean
  danger?: boolean
  action?: ReactNode
}) {
  return (
    <main className='auth-screen'>
      <section className='auth-card' aria-live='polite'>
        <div className='auth-language'>
          <LanguageControl />
        </div>
        <span className={`auth-mark ${danger ? 'is-danger' : ''}`} aria-hidden='true'>
          {busy ? (
            <LoaderCircle className='spin' size={23} />
          ) : danger ? (
            <AlertTriangle size={22} />
          ) : (
            <LockKeyhole size={22} />
          )}
        </span>
        <span className='auth-product'>Computinger</span>
        <h1>{title}</h1>
        <p>{description}</p>
        {action ? <div className='auth-action'>{action}</div> : null}
      </section>
    </main>
  )
}

export function AuthStart() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const [failureKey, setFailureKey] = useState<string | null>(null)
  const started = useRef(false)
  const status = useQuery({ queryKey: ['status'], queryFn: api.status, retry: false })

  useEffect(() => {
    if (!status.data || started.current) return
    started.current = true
    const returnTo = safeReturnTo(search.get('returnTo'))
    if (!status.data.features.developerCenter) {
      setFailureKey('Developer Center is disabled on this control plane.')
      return
    }
    if (status.data.authentication.mode !== 'sso') {
      navigate(returnTo, { replace: true })
      return
    }
    const { authorizeUrl, callbackPath, clientId } = status.data.authentication
    if (!authorizeUrl || !clientId) {
      setFailureKey('Computinger SSO is not configured for this Developer Center.')
      return
    }

    void (async () => {
      try {
        const state = randomBase64Url(32)
        const verifier = randomBase64Url(64)
        const challenge = await pkceChallenge(verifier)
        const redirectUri = new URL(callbackPath, window.location.origin).toString()
        const record: StoredAuthorizationRequest = {
          state,
          verifier,
          redirectUri,
          returnTo,
          createdAt: Date.now(),
        }
        window.sessionStorage.setItem(
          `${requestStoragePrefix}${state}`,
          JSON.stringify(record)
        )
        const authorization = new URL(authorizeUrl)
        authorization.searchParams.set('response_type', 'code')
        authorization.searchParams.set('client_id', clientId)
        authorization.searchParams.set('redirect_uri', redirectUri)
        authorization.searchParams.set('state', state)
        authorization.searchParams.set('code_challenge', challenge)
        authorization.searchParams.set('code_challenge_method', 'S256')
        authorization.searchParams.set('lang', i18n.resolvedLanguage ?? i18n.language)
        window.location.assign(authorization.toString())
      } catch {
        setFailureKey('Unable to start the secure sign-in flow.')
      }
    })()
  }, [navigate, search, status.data])

  if (status.isError || failureKey) {
    return (
      <AuthFrame
        danger
        title={t('Developer Center sign-in is unavailable')}
        description={
          failureKey
            ? t(failureKey)
            : t('The Developer Center status endpoint could not be reached.')
        }
        action={
          <a className='button button-secondary' href='https://www.computinger.com'>
            {t('Back to Computinger')}
          </a>
        }
      />
    )
  }
  return (
    <AuthFrame
      busy
      title={t('Connecting to Computinger')}
      description={t('A secure one-time authorization request is being prepared.')}
    />
  )
}

export function AuthCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search] = useSearchParams()
  const [failureKey, setFailureKey] = useState<string | null>(null)
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true
    const code = search.get('code')
    const state = search.get('state')
    if (!code || !state) {
      setFailureKey('The authorization response is missing its code or state.')
      return
    }
    const storageKey = `${requestStoragePrefix}${state}`
    const serialized = window.sessionStorage.getItem(storageKey)
    window.sessionStorage.removeItem(storageKey)
    if (!serialized) {
      setFailureKey('The authorization request is missing or was already used.')
      return
    }

    void (async () => {
      try {
        const request = JSON.parse(serialized) as StoredAuthorizationRequest
        if (
          request.state !== state ||
          typeof request.verifier !== 'string' ||
          typeof request.redirectUri !== 'string' ||
          typeof request.createdAt !== 'number' ||
          Date.now() - request.createdAt > requestLifetimeMs
        ) {
          throw new Error('Invalid stored authorization request')
        }
        const session = await api.exchangeSsoCode({
          code,
          codeVerifier: request.verifier,
          redirectUri: request.redirectUri,
        })
        queryClient.setQueryData(['session'], session)
        if (
          supportedLanguages.includes(
            session.account.language as (typeof supportedLanguages)[number]
          )
        ) {
          await i18n.changeLanguage(session.account.language)
        }
        navigate(safeReturnTo(request.returnTo), { replace: true })
      } catch (error) {
        if (error instanceof ApiClientError && error.code === 'developer_access_revoked') {
          setFailureKey(
            'Developer Center access has been revoked for this Computinger account.'
          )
          return
        }
        if (
          error instanceof ApiClientError &&
          ['developer_access_required', 'signup_disabled'].includes(error.code)
        ) {
          setFailureKey(
            'This Computinger account has not been approved for Developer Center access.'
          )
          return
        }
        setFailureKey('The one-time authorization could not be completed. Start again.')
      }
    })()
  }, [navigate, queryClient, search])

  if (failureKey) {
    return (
      <AuthFrame
        danger
        title={t('Developer Center sign-in failed')}
        description={t(failureKey)}
        action={
          <button
            className='button button-primary'
            type='button'
            onClick={() => navigate('/auth/start', { replace: true })}
          >
            {t('Start again')}
          </button>
        }
      />
    )
  }
  return (
    <AuthFrame
      busy
      title={t('Verifying your Computinger account')}
      description={t('The one-time authorization code is being exchanged securely.')}
    />
  )
}

export function AuthenticationBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const returnTo = `${location.pathname}${location.search}${location.hash}`
  const session = useQuery({
    queryKey: ['session'],
    queryFn: api.me,
    retry: false,
    staleTime: 30_000,
  })

  useEffect(() => {
    const requireAuthentication = () => {
      const target = new URL('/auth/start', window.location.origin)
      target.searchParams.set('returnTo', safeReturnTo(returnTo))
      navigate(`${target.pathname}${target.search}`, { replace: true })
    }
    window.addEventListener('computinger:authentication-required', requireAuthentication)
    return () =>
      window.removeEventListener(
        'computinger:authentication-required',
        requireAuthentication
      )
  }, [navigate, returnTo])

  if (session.isPending) {
    return (
      <AuthFrame
        busy
        title={t('Loading Developer Center')}
        description={t('Your Developer Center session is being verified.')}
      />
    )
  }
  if (session.error instanceof ApiClientError && session.error.status === 401) {
    const parameters = new URLSearchParams({ returnTo: safeReturnTo(returnTo) })
    return <Navigate replace to={`/auth/start?${parameters.toString()}`} />
  }
  if (
    session.error instanceof ApiClientError &&
    ['developer_access_required', 'developer_access_revoked'].includes(session.error.code)
  ) {
    const revoked = session.error.code === 'developer_access_revoked'
    return (
      <AuthFrame
        danger
        title={t(revoked ? 'Developer Center access was revoked' : 'Developer Center access is not enabled')}
        description={t(
          revoked
            ? 'Developer Center access has been revoked for this Computinger account.'
            : 'This Computinger account has not been approved for Developer Center access.'
        )}
        action={
          <a className='button button-secondary' href='https://www.computinger.com'>
            {t('Back to Computinger')}
          </a>
        }
      />
    )
  }
  if (session.isError) {
    return (
      <AuthFrame
        danger
        title={t('Developer Center is unavailable')}
        description={t('The authenticated control plane could not be reached.')}
        action={
          <button
            className='button button-secondary'
            type='button'
            onClick={() => void session.refetch()}
          >
            {t('Retry')}
          </button>
        }
      />
    )
  }
  return children
}
